<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Title + detail paired with an optional image or YouTube video. Each
 * instance is one section — a page composes several to build a "why us" /
 * feature-list flow, choosing the media position per instance rather than
 * auto-alternating.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const YOUTUBE_PATTERNS = [
  /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
  /youtu\.be\/([a-zA-Z0-9_-]{11})/,
  /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/
]

const youtubeId = computed(() => {
  const url = props.data.media_url
  if (!url) return null
  for (const pattern of YOUTUBE_PATTERNS) {
    const match = url.match(pattern)
    if (match?.[1]) return match[1]
  }
  return null
})

const hasMedia = computed(() => {
  if (props.data.media_type === 'image') return !!props.data.media_url
  if (props.data.media_type === 'youtube') return !!youtubeId.value
  return false
})

const layout = computed(() => (hasMedia.value ? props.data.layout ?? 'right' : null))

const isExternalLink = (url: string) => /^(https?:|mailto:|tel:)/.test(url)
</script>

<template>
  <div>
    <div
      v-if="layout"
      class="grid items-center gap-10"
      :class="layout === 'full' ? 'grid-cols-1 gap-8' : 'lg:grid-cols-2'"
    >
      <!-- Text -->
      <div
        :class="[
          layout === 'full' ? 'order-2 mx-auto max-w-2xl text-center' : '',
          layout === 'left' ? 'lg:order-2' : layout === 'right' ? 'lg:order-1' : ''
        ]"
      >
        <h2 v-if="data.headline" class="site-display text-3xl tracking-tight sm:text-4xl">
          {{ data.headline }}
        </h2>
        <p
          v-if="data.subtext"
          class="mt-4 text-lg leading-relaxed text-[var(--site-muted)]"
          style="white-space: pre-wrap"
        >
          {{ data.subtext }}
        </p>
      </div>

      <!-- Media -->
      <div :class="layout === 'left' ? 'lg:order-1' : layout === 'right' ? 'lg:order-2' : 'order-1'">
        <NuxtLink
          v-if="data.media_type === 'image' && data.media_link"
          :to="data.media_link"
          :external="isExternalLink(data.media_link)"
          target="_blank"
          rel="noopener noreferrer"
          class="block overflow-hidden rounded-[var(--site-radius)] shadow-lg"
        >
          <img
            :src="data.media_url!"
            :alt="data.headline || ''"
            class="w-full object-cover"
            :style="{ maxHeight: layout === 'full' ? '480px' : '420px' }"
            loading="lazy"
          >
        </NuxtLink>
        <img
          v-else-if="data.media_type === 'image'"
          :src="data.media_url!"
          :alt="data.headline || ''"
          class="w-full overflow-hidden rounded-[var(--site-radius)] object-cover shadow-lg"
          :style="{ maxHeight: layout === 'full' ? '480px' : '420px' }"
          loading="lazy"
        >
        <div
          v-else-if="data.media_type === 'youtube'"
          class="overflow-hidden rounded-[var(--site-radius)] shadow-lg"
          style="aspect-ratio: 16/9"
        >
          <iframe
            :src="`https://www.youtube.com/embed/${youtubeId}?rel=0`"
            class="h-full w-full"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
        </div>
      </div>
    </div>

    <!-- No media — centered text only -->
    <div v-else class="mx-auto max-w-2xl text-center">
      <h2 v-if="data.headline" class="site-display text-3xl tracking-tight sm:text-4xl">
        {{ data.headline }}
      </h2>
      <p
        v-if="data.subtext"
        class="mt-4 text-lg leading-relaxed text-[var(--site-muted)]"
        style="white-space: pre-wrap"
      >
        {{ data.subtext }}
      </p>
    </div>
  </div>
</template>
